import type { ToastServiceMethods } from 'primevue'

import { defineStore } from 'pinia'
import { ref } from 'vue'

import type {
  LoggedToast,
  LoggedToastMaintenance,
  LoggedToastState,
  ToastAction
} from '@/domains/notifications/store/use-toast-store/types'

import { useLocales } from '@/locales/useLocales.ts'
import { generateComplexId } from '@/utils/math-util/mathUtil.ts'

const EXPIRING_STATES: LoggedToastState[] = ['once', 'success', 'error', 'warn']

/**
 * A toast leaves on its own only when its state expires and it holds no action.
 * An action is a control, so it stays until the user closes it or the operation ends (WCAG 2.2.1).
 */
const expiresOnItsOwn = (state: LoggedToastState, action?: ToastAction): boolean =>
  EXPIRING_STATES.includes(state) && !action

export const useToastStore = defineStore('toast', () => {
  const service = ref<ToastServiceMethods>()
  const history = ref<Record<string, LoggedToast[]>>({})

  const setService = (value: ToastServiceMethods) => {
    service.value = value
  }

  /**
   * Calculates how long a toast message should be displayed based on the message length.
   *
   * The duration scales linearly between a minimum (6000ms) and maximum (12000ms) display time,
   * based on the number of characters in the message. This ensures short messages disappear quickly,
   * while longer ones remain on screen long enough to be read.
   *
   * - Messages with 0–100 characters scale from 6000ms to 12000ms.
   * - Messages longer than 100 characters are capped at 12000ms.
   * - Messages shorter than 100 characters receive proportionally less time.
   *
   * @param {string} message - The toast message to be displayed.
   * @returns {number} The duration in milliseconds (between 6000ms and 12000ms).
   */
  const getToastDuration = (message: string): number => {
    const minDuration = 6000
    const maxDuration = 12000
    const maxChars = 100
    const clampedLength = Math.min(message.length, maxChars)
    const ratio = clampedLength / maxChars
    return Math.round(minDuration + (maxDuration - minDuration) * ratio)
  }

  const addError = ({ details }: { details?: string } = {}) => {
    add({
      state: 'once',
      severity: 'error',
      detail: details || 'Something went wrong',
      summary: 'Oops.'
    })
  }

  const add = (payload: LoggedToast = {}) => {
    const { id = generateComplexId(), timestamp = Date.now(), severity = 'success', state = 'once', ...rest } = payload

    const locales = useLocales().quickDictionary.value.toast as Record<string, { summary: string; detail: string }>
    const defaultContent = locales[severity] ?? { summary: '', detail: '' }

    const _messageDetail = payload.detail || defaultContent.detail

    const toastPayload: LoggedToastMaintenance = {
      id: `${state}-${id}`,
      timestamp,
      severity,
      state,
      summary: defaultContent.summary,
      detail: defaultContent.detail,
      ...(expiresOnItsOwn(state, rest.action) ? { life: getToastDuration(_messageDetail) } : {}),
      ...rest
    }

    const existing = history.value[id] || []

    history.value[id] = [...existing, toastPayload]

    toastMaintenance(id, toastPayload)
    service.value?.add(toastPayload)
  }

  const toastMaintenance = (operationId: string, payload: LoggedToastMaintenance) => {
    if (['success', 'error', 'warn'].includes(payload.state)) {
      history.value[operationId]?.forEach(toast => {
        if (!toast.life && toast.id !== payload.id) {
          service.value?.remove(toast)
        }
      })
    }
  }

  const clearOperation = (operationId: string) => {
    history.value[operationId]?.forEach(toast => {
      service.value?.remove(toast)
    })
    delete history.value[operationId]
  }

  const resetStore = () => {
    history.value = {}
  }

  return {
    setService,
    resetStore,
    add,
    addError,
    clearOperation,
    history
  }
})
